document.documentElement.classList.remove('no-js');

const ZODIAC_KEY_ALIASES = {
  mouse: 'rat', buffalo: 'ox', bull: 'ox', cow: 'ox', sheep: 'goat', ram: 'goat',
  chicken: 'rooster', cock: 'rooster', boar: 'pig',
  '鼠': 'rat', '牛': 'ox', '虎': 'tiger', '兔': 'rabbit', '龙': 'dragon', '蛇': 'snake',
  '马': 'horse', '羊': 'goat', '猴': 'monkey', '鸡': 'rooster', '狗': 'dog', '猪': 'pig'
};
const normalizeZodiacKey = (animal) => {
  const raw = String(animal || '').trim().toLowerCase();
  return ZODIAC_KEY_ALIASES[raw] || raw;
};

const setZodiacIcon = (target, animal) => {
  if (!target || !animal) return;
  const iconSet = document.getElementById('PhoseiZodiacIconSet');
  const source = iconSet?.content.querySelector(`[data-zodiac-icon-source="${normalizeZodiacKey(animal)}"] svg`);
  if (source) target.replaceChildren(source.cloneNode(true));
};

const setBaziZodiacImage = (target, animal) => {
  if (!target || !animal) return;
  const artSet = document.getElementById('PhoseiBaziArtSet');
  const source = artSet?.content.querySelector(`[data-zodiac-image-source="${normalizeZodiacKey(animal)}"]`);
  if (!source?.dataset.src) return;
  target.setAttribute('src', source.dataset.src);
  target.alt = `${animal} Chinese zodiac illustration`;
  target.hidden = false;
  target.removeAttribute('hidden');
  target.style.removeProperty('display');
};

const setBaziElementImage = (target, element) => {
  if (!target || !element) return;
  const artSet = document.getElementById('PhoseiBaziArtSet');
  const source = artSet?.content.querySelector(`[data-element-image-source="${String(element).toLowerCase()}"]`);
  if (!source?.dataset.src) return;
  target.src = source.dataset.src;
  target.alt = `${element} Five Element illustration`;
  target.hidden = false;
};

const setBaziElementIcon = (target, element) => {
  if (!target || !element) return;
  const artSet = document.getElementById('PhoseiBaziArtSet');
  const source = artSet?.content.querySelector(`[data-element-icon-source="${String(element).toLowerCase()}"] svg`);
  if (source) target.replaceChildren(source.cloneNode(true));
};

/* Interpretation tables — classical imagery for the ten Day Masters, the five
   element relations to the Day Master, and what an absent element tends to mean.
   Framed as traditional tendencies, never as guarantees. */
const BAZI_ELEMENT_ORDER = ['Wood', 'Fire', 'Earth', 'Metal', 'Water'];
const baziElementRelation = (dayElement, otherElement) => {
  const self = BAZI_ELEMENT_ORDER.indexOf(dayElement);
  const other = BAZI_ELEMENT_ORDER.indexOf(otherElement);
  if (self < 0 || other < 0) return '';
  if (self === other) return 'peer';
  if ((self + 1) % 5 === other) return 'output';
  if ((other + 1) % 5 === self) return 'resource';
  if ((self + 2) % 5 === other) return 'reward';
  return 'influence';
};
const BAZI_RELATION_THEMES = {
  peer: 'independence, peers, and standing on your own ground',
  output: 'expression, skills, and being recognised for what you produce',
  resource: 'learning, support, and the backing that recharges you',
  reward: 'opportunity and practical returns that answer to steady effort',
  influence: 'structure, responsibility, and pressure that can mature into authority'
};
const BAZI_DAY_MASTER_PROFILES = {
  '甲': 'traditionally pictured as the tall, upright tree: you tend to set a direction, grow toward it steadily, and hold your shape under pressure. Classically this Wood welcomes a little carving — the challenges that shape you are often the ones that make you most useful.',
  '乙': 'traditionally pictured as climbing vines and flowers: flexible, resourceful, and able to grow around obstacles rather than through them. Your persistence looks gentle from the outside, but it rarely lets go of what it values.',
  '丙': 'traditionally pictured as the sun: warm, visible, and generous with energy. You tend to lift the mood of a room and work best when your light lands somewhere — unwitnessed effort can feel draining.',
  '丁': 'traditionally pictured as the lamp flame: focused, precise warmth that illuminates details others miss. You tend to burn steadily for the people and projects you choose, rather than broadly for everything.',
  '戊': 'traditionally pictured as the mountain and the city wall: steady, protective, and hard to move once committed. People tend to lean on you — the classical advice is to choose consciously what the wall protects.',
  '己': 'traditionally pictured as garden soil: nurturing, patient, and quietly essential. You tend to grow people and projects well, and your results often ripen later — and last longer — than louder charts.',
  '庚': 'traditionally pictured as raw ore and the sword: decisive, principled, and at your best when there is something real to cut through. Work that rewards clean judgment suits this Metal.',
  '辛': 'traditionally pictured as refined jade and jewellery: precise, quality-minded, and sensitive to how things are finished. You notice the flaw others accept — a gift when it edits, a weight when it only criticises.',
  '壬': 'traditionally pictured as the river and the sea: momentum, breadth, and an instinct to keep moving and connecting. For this Water, direction matters more than speed.',
  '癸': 'traditionally pictured as rain, dew, and spring water: subtle, perceptive, and quietly persistent. Given time, you tend to reach places that force cannot.'
};
const BAZI_DAY_MASTER_PINYIN = { Jia: '甲', Yi: '乙', Bing: '丙', Ding: '丁', Wu: '戊', Ji: '己', Geng: '庚', Xin: '辛', Ren: '壬', Gui: '癸' };
const baziDayMasterProfile = (stem) => {
  if (!stem) return '';
  return BAZI_DAY_MASTER_PROFILES[stem.hanzi] || BAZI_DAY_MASTER_PROFILES[BAZI_DAY_MASTER_PINYIN[stem.pinyin]] || '';
};
const BAZI_MISSING_ELEMENT_NOTES = {
  Wood: 'long-range planning and growth may be something you build deliberately rather than by default — putting goals in writing tends to repay you.',
  Fire: 'visibility may not come automatically — deliberately showing your work matters more for you than for most people.',
  Earth: 'grounding and routine may need conscious effort — stable anchors in place, schedule, and people tend to repay you quickly.',
  Metal: 'boundaries and finishing may be the muscle to train — deciding what to cut is worth scheduling, not just intending.',
  Water: 'pausing to observe before acting may not be your default — building in a deliberate "look first" step tends to save rework.'
};

const initLocalPreviewLinks = (scope = document) => {
  if (!['127.0.0.1', 'localhost'].includes(window.location.hostname)) return;

  const previewRoutes = {
    '/pages/about': 'about-preview',
    '/pages/is-this-you': 'is-this-you-preview',
    '/pages/our-story': 'our-story-preview',
    '/pages/testimonials': 'testimonials-preview',
    '/pages/faq': 'faq-preview',
    '/pages/learning': 'learning-preview',
    '/pages/what-is-bazi': 'what-is-bazi-preview',
    '/pages/four-pillars': 'four-pillars-preview',
    '/pages/how-to-read-bazi-chart': 'how-to-read-bazi-chart-preview',
    '/pages/bazi-day-master': 'bazi-day-master-preview',
    '/pages/five-elements': 'five-elements-preview',
    '/pages/chinese-zodiac': 'chinese-zodiac-preview',
    '/pages/chinese-zodiac-years': 'chinese-zodiac-years-preview',
    '/pages/chinese-zodiac-compatibility': 'chinese-zodiac-compatibility-preview',
    '/pages/bazi-vs-western-astrology': 'bazi-vs-western-astrology-preview',
    '/pages/free-services': 'free-preview',
    '/pages/free-bazi-chart': 'free-bazi-preview',
    '/pages/contact': 'contact-preview'
  };

  scope.querySelectorAll('a[href]').forEach((link) => {
    const url = new URL(link.href, window.location.origin);
    const preview = previewRoutes[url.pathname];
    if (!preview || url.origin !== window.location.origin) return;
    link.href = `/?view=${preview}${url.hash}`;
  });
};

const initReveal = (scope = document) => {
  const elements = scope.querySelectorAll('[data-reveal]:not(.is-visible)');
  if (!elements.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible', 'in');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12 }
  );

  elements.forEach((element) => observer.observe(element));
};

const initMobileNavigation = (scope = document) => {
  scope.querySelectorAll('.pp-mobile-nav').forEach((menu) => {
    menu.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => menu.removeAttribute('open'));
    });
  });
};

const initDropdownNavigation = (scope = document) => {
  const dropdowns = [...scope.querySelectorAll('.pp-nav-dropdown')];
  dropdowns.forEach((dropdown) => {
    dropdown.addEventListener('toggle', () => {
      if (!dropdown.open) return;
      dropdowns.forEach((other) => {
        if (other !== dropdown) other.removeAttribute('open');
      });
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
  });
};

const initFooterYear = (scope = document) => {
  scope.querySelectorAll('[data-current-year]').forEach((element) => {
    element.textContent = new Date().getFullYear();
  });
};

const wrapCanvasText = (context, text, x, y, maxWidth, lineHeight, maxLines = Infinity) => {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (context.measureText(next).width <= maxWidth || !line) {
      line = next;
    } else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  const visible = lines.slice(0, maxLines);
  visible.forEach((entry, index) => context.fillText(entry, x, y + (index * lineHeight)));
  return visible.length * lineHeight;
};

const loadCanvasImage = async (source) => {
  if (!source) return null;
  try {
    const response = await fetch(source, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const image = await new Promise((resolve, reject) => {
      const asset = new Image();
      asset.onload = () => resolve(asset);
      asset.onerror = reject;
      asset.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    return image;
  } catch (_) {
    return null;
  }
};

const downloadBaziReport = async (result, responseBody, trigger) => {
  if (!result?.classList.contains('has-result') || !responseBody?.chart) return;
  const triggerLabel = trigger?.querySelector('span');
  const originalLabel = triggerLabel?.textContent;
  if (trigger) {
    trigger.disabled = true;
    if (triggerLabel) triggerLabel.textContent = 'PREPARING YOUR CHART…';
  }

  try {
    const chart = responseBody.chart;
    const analysis = responseBody.analysis || {};
    const hasHour = Boolean(chart.hour);
    const cycles = Array.isArray(responseBody.luck_cycles?.cycles)
      ? responseBody.luck_cycles.cycles.filter((cycle) => !cycle.is_start_segment && cycle.gan_zhi)
      : [];
    const width = 1440;
    const margin = 84;
    const cardGap = 18;
    const cardWidth = hasHour ? (width - (margin * 2) - (cardGap * 3)) / 4 : (width - (margin * 2) - (cardGap * 2)) / 3;
    const cycleColumns = Math.max(cycles.length, 1);
    const height = Math.max(1660, 1360 + (cycles.length ? 190 : 0) + (hasHour ? 0 : 80));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Your browser could not prepare the chart image.');

    context.fillStyle = '#fbf8f1';
    context.fillRect(0, 0, width, height);
    const wash = context.createLinearGradient(0, 0, width, height);
    wash.addColorStop(0, 'rgba(244,233,210,.78)');
    wash.addColorStop(.54, 'rgba(255,253,249,0)');
    wash.addColorStop(1, 'rgba(228,237,232,.46)');
    context.fillStyle = wash;
    context.fillRect(0, 0, width, height);

    const zodiacImage = result.querySelector('[data-bazi-zodiac-image]')?.currentSrc || result.querySelector('[data-bazi-zodiac-image]')?.src;
    const [zodiacArt, qrArt] = await Promise.all([
      loadCanvasImage(zodiacImage),
      loadCanvasImage(`https://api.qrserver.com/v1/create-qr-code/?size=220x220&format=png&data=${encodeURIComponent('https://phosei.com')}`)
    ]);
    if (zodiacArt) {
      context.save();
      context.globalAlpha = .16;
      context.drawImage(zodiacArt, width - 460, 54, 350, 350);
      context.restore();
    }

    context.fillStyle = '#a9422c';
    context.font = '700 18px Arial, sans-serif';
    context.letterSpacing = '2px';
    context.fillText('PHOSEI · PERSONAL BAZI CHART', margin, 88);
    context.fillStyle = '#29231e';
    context.font = '700 58px Georgia, serif';
    context.fillText(hasHour ? 'Your Four Pillars' : 'Your Three Pillars', margin, 160);
    context.fillStyle = '#74685b';
    context.font = '400 20px Arial, sans-serif';
    const generatedAt = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date());
    context.fillText(`Generated ${generatedAt}`, margin, 198);
    context.strokeStyle = 'rgba(145,113,71,.42)';
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(margin, 232);
    context.lineTo(width - margin, 232);
    context.stroke();

    let y = 278;
    if (!hasHour) {
      context.fillStyle = '#f5eee1';
      context.strokeStyle = 'rgba(169,66,44,.34)';
      context.lineWidth = 1;
      context.fillRect(margin, y, width - (margin * 2), 112);
      context.strokeRect(margin, y, width - (margin * 2), 112);
      context.fillStyle = '#8e3c29';
      context.font = '700 19px Arial, sans-serif';
      context.fillText('YOUR THREE PILLARS', margin + 24, y + 34);
      context.fillStyle = '#5d5348';
      context.font = '400 18px Arial, sans-serif';
      wrapCanvasText(context, 'This chart begins with the Year, Month, and Day of birth. If you discover your birth time later, the Hour Pillar can add another layer to the picture and bring the timing section into focus.', margin + 24, y + 67, width - (margin * 2) - 48, 25, 2);
      y += 148;
    }

    const labels = { year: 'YEAR', month: 'MONTH', day: 'DAY', hour: 'HOUR' };
    const order = hasHour ? ['year', 'month', 'day', 'hour'] : ['year', 'month', 'day'];
    order.forEach((key, index) => {
      const pillar = chart[key];
      const x = margin + (index * (cardWidth + cardGap));
      context.fillStyle = key === 'day' ? '#fffaf2' : '#f6efe4';
      context.strokeStyle = key === 'day' ? '#b7442f' : 'rgba(145,113,71,.42)';
      context.lineWidth = key === 'day' ? 3 : 1;
      context.fillRect(x, y, cardWidth, 300);
      context.strokeRect(x, y, cardWidth, 300);
      context.fillStyle = '#a9422c';
      context.font = '700 16px Arial, sans-serif';
      context.fillText(labels[key], x + 22, y + 34);
      context.fillStyle = '#29231e';
      context.font = '700 42px Georgia, serif';
      context.fillText(`${pillar.stem.hanzi}${pillar.branch.hanzi}`, x + 22, y + 95);
      context.font = '700 21px Arial, sans-serif';
      context.fillText(`${pillar.stem.yin_yang} ${pillar.stem.element}`, x + 22, y + 138);
      context.fillStyle = '#5d5348';
      context.font = '400 18px Arial, sans-serif';
      context.fillText(`${pillar.stem.pinyin} · Heavenly Stem`, x + 22, y + 171);
      context.strokeStyle = 'rgba(145,113,71,.26)';
      context.lineWidth = 1;
      context.beginPath();
      context.moveTo(x + 22, y + 192);
      context.lineTo(x + cardWidth - 22, y + 192);
      context.stroke();
      context.fillStyle = '#29231e';
      context.font = '700 21px Arial, sans-serif';
      context.fillText(`${pillar.branch.yin_yang} ${pillar.branch.element}`, x + 22, y + 228);
      context.fillStyle = '#5d5348';
      context.font = '400 18px Arial, sans-serif';
      context.fillText(`${pillar.branch.pinyin} · Earthly Branch`, x + 22, y + 260);
    });

    y += 352;
    context.fillStyle = '#a9422c';
    context.font = '700 16px Arial, sans-serif';
    context.fillText('PERSONAL OVERVIEW', margin, y);
    y += 38;
    context.fillStyle = '#29231e';
    context.font = '700 30px Georgia, serif';
    const personalisedCopy = result.querySelector('[data-bazi-analysis-method] span')?.textContent || result.querySelector('[data-bazi-analysis-copy]')?.textContent || analysis.plain_english || '';
    y += wrapCanvasText(context, personalisedCopy, margin, y, width - (margin * 2), 42, 4) + 18;

    context.fillStyle = '#a9422c';
    context.font = '700 16px Arial, sans-serif';
    context.fillText('FIVE ELEMENTS', margin, y);
    y += 26;
    const balance = Array.isArray(analysis.element_balance) ? analysis.element_balance : [];
    const elementWidth = (width - (margin * 2) - (cardGap * 4)) / 5;
    balance.forEach((entry, index) => {
      const x = margin + (index * (elementWidth + cardGap));
      const count = Number(entry.count) || 0;
      context.fillStyle = '#fffdf9';
      context.strokeStyle = 'rgba(145,113,71,.34)';
      context.fillRect(x, y, elementWidth, 96);
      context.strokeRect(x, y, elementWidth, 96);
      context.fillStyle = '#5d5348';
      context.font = '700 16px Arial, sans-serif';
      context.fillText(entry.element || 'Element', x + 16, y + 31);
      context.fillStyle = '#a9422c';
      context.font = '700 32px Georgia, serif';
      context.fillText(String(count), x + 16, y + 76);
      context.fillStyle = '#d9cab3';
      context.fillRect(x + 58, y + 64, elementWidth - 76, 7);
      context.fillStyle = '#a9422c';
      context.fillRect(x + 58, y + 64, Math.min(elementWidth - 76, (elementWidth - 76) * (count / (hasHour ? 4 : 3))), 7);
    });
    y += 142;

    if (cycles.length) {
      context.fillStyle = '#a9422c';
      context.font = '700 16px Arial, sans-serif';
      context.fillText('HOROSCOPE CYCLES', margin, y);
      y += 28;
      const cycleWidth = (width - (margin * 2)) / cycleColumns;
      const currentYear = new Date().getFullYear();
      cycles.forEach((cycle, index) => {
        const x = margin + (index * cycleWidth);
        const cycleY = y;
        const isCurrent = currentYear >= Number(cycle.start_year) && currentYear <= Number(cycle.end_year);
        context.fillStyle = isCurrent ? '#fdf0dd' : '#fffdf9';
        context.strokeStyle = 'rgba(145,113,71,.34)';
        context.fillRect(x, cycleY, cycleWidth, 158);
        context.strokeRect(x, cycleY, cycleWidth, 158);
        if (isCurrent) {
          context.fillStyle = '#a9422c';
          context.fillRect(x, cycleY, cycleWidth, 4);
        }
        context.fillStyle = '#a9422c';
        context.font = '700 14px Arial, sans-serif';
        context.fillText(String(cycle.start_year || ''), x + 14, cycleY + 27);
        context.fillStyle = '#75695d';
        context.font = '600 13px Arial, sans-serif';
        context.fillText(`Ages ${cycle.start_age}–${cycle.end_age}`, x + 14, cycleY + 49);
        context.fillStyle = '#29231e';
        context.font = '700 16px Arial, sans-serif';
        context.fillText(`${cycle.stem?.pinyin || ''} ${cycle.stem?.element || ''}`.trim(), x + 14, cycleY + 82);
        context.fillText(`${cycle.branch?.pinyin || ''} ${cycle.branch?.element || ''}`.trim(), x + 14, cycleY + 104);
        context.fillStyle = '#a9422c';
        context.font = '600 21px Arial, sans-serif';
        context.fillText(cycle.gan_zhi, x + 14, cycleY + 138);
      });
      y += 196;
    }

    const footerY = height - 214;
    context.strokeStyle = 'rgba(145,113,71,.42)';
    context.beginPath();
    context.moveTo(margin, footerY - 24);
    context.lineTo(width - margin, footerY - 24);
    context.stroke();
    context.fillStyle = '#29231e';
    context.font = '700 28px Georgia, serif';
    context.fillText('PHOSEI', margin, footerY + 18);
    context.fillStyle = '#74685b';
    context.font = '400 18px Arial, sans-serif';
    context.fillText('Discover Yourself, Embrace Your Journey.', margin, footerY + 52);
    context.fillText('phosei.com', margin, footerY + 82);
    if (qrArt) context.drawImage(qrArt, width - margin - 150, footerY - 4, 150, 150);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Your browser could not create the chart image.');
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `phosei-${hasHour ? 'four' : 'three'}-pillars-chart.png`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    window.alert(error?.message || 'We could not download the chart image. Please try again.');
  } finally {
    if (trigger) {
      trigger.disabled = false;
      if (triggerLabel) triggerLabel.textContent = originalLabel || 'DOWNLOAD YOUR CHART';
    }
  }
};

const initBaziCalculator = (scope = document) => {
  scope.querySelectorAll('[data-bazi-calculator]').forEach((calculator) => {
    const form = calculator.querySelector('[data-bazi-form]');
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    const result = calculator.querySelector('.bazi-result');
    const message = calculator.querySelector('[data-bazi-message]');
    const submit = calculator.querySelector('[data-bazi-submit]');
    const next = calculator.querySelector('[data-bazi-next]');
    const back = calculator.querySelector('[data-bazi-back]');
    const steps = [...calculator.querySelectorAll('[data-bazi-step]')];
    const progress = [...calculator.querySelectorAll('[data-bazi-progress]')];
    const lock = calculator.querySelector('[data-bazi-lock]');
    const reportActions = calculator.querySelector('[data-bazi-report-actions]');
    const exportReport = calculator.querySelector('[data-bazi-export-report]');
    const timeInput = form.querySelector('input[name="birth_time"]');
    const timeUnknown = form.querySelector('[data-bazi-time-unknown]');
    const endpoint = calculator.dataset.apiUrl;
    let lastResponse = null;

    exportReport?.addEventListener('click', () => downloadBaziReport(result, lastResponse, exportReport));

    const updateBirthTimeChoice = () => {
      const isUnknown = Boolean(timeUnknown?.checked);
      if (!timeInput) return;
      timeInput.disabled = isUnknown;
      timeInput.required = !isUnknown;
      if (isUnknown) timeInput.value = '';
    };
    timeUnknown?.addEventListener('change', updateBirthTimeChoice);
    updateBirthTimeChoice();

    const showStep = (number) => {
      steps.forEach((step) => {
        const isActive = step.dataset.baziStep === String(number);
        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
      });
      progress.forEach((item) => item.classList.toggle('is-current', item.dataset.baziProgress === String(number)));
      message.textContent = '';
      if (number === 2) calculator.querySelector('input[name="email"]')?.focus({ preventScroll: true });
    };

    const validateStep = (step) => {
      const controls = [...step.querySelectorAll('input, select, textarea')];
      const invalid = controls.find((control) => !control.checkValidity());
      if (!invalid) return true;
      invalid.reportValidity();
      return false;
    };

    next?.addEventListener('click', () => {
      const firstStep = calculator.querySelector('[data-bazi-step="1"]');
      if (firstStep && validateStep(firstStep)) showStep(2);
    });
    back?.addEventListener('click', () => showStep(1));

    const setLoading = (loading) => {
      submit.disabled = loading;
      submit.textContent = loading ? 'CALCULATING YOUR CHART…' : 'GET MY BAZI CHART';
      result.classList.toggle('is-loading', loading);
      result.setAttribute('aria-busy', String(loading));
    };

    const renderPillar = (key, pillar) => {
      const card = calculator.querySelector(`[data-pillar="${key}"]`);
      if (!card || !pillar) return;
      const setCardText = (selector, value) => {
        const target = card.querySelector(selector);
        if (target) target.textContent = value;
      };
      card.dataset.stemElement = String(pillar.stem.element || '').toLowerCase();
      card.dataset.branchElement = String(pillar.branch.element || '').toLowerCase();
      setBaziElementImage(card.querySelector('[data-stem-element-image]'), pillar.stem.element);
      setBaziElementImage(card.querySelector('[data-branch-element-image]'), pillar.branch.element);
      setCardText('[data-stem-primary]', `${pillar.stem.yin_yang} ${pillar.stem.element}`);
      setCardText('[data-stem-english]', pillar.stem.pinyin);
      setCardText('[data-stem-hanzi]', pillar.stem.hanzi);
      setCardText('[data-stem-detail]', 'Heavenly Stem');
      setCardText('[data-branch-primary]', `${pillar.branch.yin_yang} ${pillar.branch.element}`);
      setCardText('[data-branch-english]', pillar.branch.pinyin);
      setCardText('[data-branch-hanzi]', pillar.branch.hanzi);
      const branchQuality = [pillar.branch.yin_yang, pillar.branch.element].filter(Boolean).join(' ');
      setCardText('[data-branch-detail]', `${branchQuality} · Earthly Branch`);
    };

    const renderBaziAnalysis = (analysis) => {
      const panel = calculator.querySelector('[data-bazi-analysis]');
      if (!panel || !analysis) return;
      if (!analysis.plain_english && !(analysis.structural_notes || []).length && !(analysis.element_balance || []).length) { panel.hidden = true; return; }
      const balance = Array.isArray(analysis.element_balance) ? analysis.element_balance : [];
      const dayMaster = analysis.day_master || {};
      const visibleCharacterCount = Number(analysis.visible_character_count) || balance.reduce((total, entry) => total + (Number(entry.count) || 0), 0);
      const highestCount = Math.max(...balance.map((entry) => Number(entry.count) || 0), 0);
      const prominent = balance.filter((entry) => (Number(entry.count) || 0) === highestCount && highestCount > 0).map((entry) => entry.element).filter(Boolean);
      const prominentLabel = prominent.join(' and ');
      const absent = balance.filter((entry) => (Number(entry.count) || 0) === 0).map((entry) => entry.element).filter(Boolean);
      const supporting = balance
        .filter((entry) => (Number(entry.count) || 0) > 0 && !prominent.includes(entry.element))
        .sort((left, right) => (Number(right.count) || 0) - (Number(left.count) || 0))[0]?.element;
      const seasonalNote = (analysis.structural_notes || []).find((note) => String(note.label || '').toLowerCase().includes('season'));
      const master = [dayMaster.pinyin, dayMaster.element].filter(Boolean).join(' ');
      const seasonalDetail = String(seasonalNote?.detail || '').replace(/^The Month Branch,\s*/, '').replace(/ sets a seasonal.*$/i, '').replace(/[,，]\s*$/, '').trim();
      const elementTraits = {
        Wood: 'growth-minded, responsive to possibility, and inclined to look for a way forward',
        Fire: 'expressive, quick to build momentum, and inclined to make ideas felt or visible',
        Earth: 'steady, practical, and inclined to make things workable for the people around you',
        Metal: 'discerning, standards-led, and inclined to refine what matters',
        Water: 'observant, adaptable, and inclined to consider several routes before committing'
      };
      const supportingTraits = {
        Wood: 'a constructive wish to develop and expand what has potential',
        Fire: 'warmth and a readiness to bring energy into the room',
        Earth: 'a grounding instinct that looks for what is reliable and useful',
        Metal: 'clear standards and an eye for what needs editing or definition',
        Water: 'flexibility and patience with complexity'
      };
      const copy = panel.querySelector('[data-bazi-analysis-copy]');
      if (copy) {
        copy.textContent = master
          ? `${master} is your Day Master. ${seasonalDetail ? `${seasonalDetail} frames the season. ` : ''}${prominentLabel ? `${prominentLabel} is most visible across your ${visibleCharacterCount} visible characters.` : ''}`.trim()
          : (analysis.plain_english || 'Your chart is ready to explore.');
      }
      const intro = panel.querySelector('[data-bazi-analysis-intro]');
      if (intro) intro.textContent = `A focused reading of the ${visibleCharacterCount || 'visible'} characters.`;
      const notes = panel.querySelector('[data-bazi-analysis-notes]');
      if (notes) {
        notes.replaceChildren();
        const dayMasterStory = baziDayMasterProfile(dayMaster);
        const overviewNotes = [
          { label: 'Day Master', detail: dayMasterStory ? `${master} — ${dayMasterStory}` : (master ? `${master} is the point of reference for this chart.` : 'Your Day Master is the point of reference for this chart.') },
          { label: 'Season', detail: seasonalDetail ? `${seasonalDetail} sets the seasonal frame.` : 'The Month Branch sets the seasonal frame.' },
          { label: 'Visible pattern', detail: prominentLabel ? `${prominentLabel} leads this first view${absent.length ? `; ${absent.join(' and ')} is not visible here` : ''}.` : 'Read the visible characters as one pattern.' }
        ];
        if (absent.length) {
          const firstAbsent = absent[0];
          overviewNotes.push({
            label: 'Not visible',
            detail: `${absent.join(' and ')} ${absent.length > 1 ? 'are' : 'is'} not visible among your eight characters — ${BAZI_MISSING_ELEMENT_NOTES[firstAbsent] || 'worth watching as a conscious habit.'} Hidden stems can still carry ${absent.length > 1 ? 'these elements' : 'it'}, which a deeper reading checks first.`
          });
        }
        overviewNotes.forEach((note) => {
          const item = document.createElement('li');
          const label = document.createElement('b');
          const detail = document.createElement('span');
          label.textContent = note.label;
          detail.textContent = note.detail;
          item.append(label, detail);
          notes.append(item);
        });
      }
      const elements = panel.querySelector('[data-bazi-analysis-elements]');
      if (elements) {
        elements.replaceChildren();
        balance.forEach((entry) => {
          const row = document.createElement('div');
          const icon = document.createElement('i');
          const heading = document.createElement('span');
          const bar = document.createElement('i');
          const count = document.createElement('b');
          const value = Number(entry.count) || 0;
          row.dataset.element = String(entry.element || '').toLowerCase();
          icon.setAttribute('aria-hidden', 'true');
          setBaziElementIcon(icon, entry.element);
          heading.textContent = entry.element || 'Element';
          bar.style.setProperty('--bazi-element-count', String(Math.min(Math.max(value, 0), 4)));
          count.textContent = String(value);
          row.append(icon, heading, bar, count);
          elements.append(row);
        });
      }
      const method = panel.querySelector('[data-bazi-analysis-method]');
      if (method) {
        method.replaceChildren();
        const label = document.createElement('b');
        const detail = document.createElement('span');
        label.className = 'bazi-reading-prompt__label';
        label.textContent = 'Your pattern at a glance';
        const dominantElement = prominent[0];
        const expression = dayMaster.yin_yang === 'Yin'
          ? 'more selective, nuanced, and responsive to atmosphere'
          : 'more direct, outward, and action-oriented';
        detail.textContent = dominantElement
          ? `With ${prominentLabel} most visible, you may come across as ${elementTraits[dominantElement] || 'attuned to the strongest pattern in your chart'}. ${master ? `Your ${master} Day Master gives that tendency a ${expression} expression.` : ''}${supporting ? ` ${supporting} also adds ${supportingTraits[supporting] || 'another useful layer to the way you approach things'}.` : ''}`.trim()
          : (master ? `Your ${master} Day Master is the reference point for the way you meet the rest of the chart.` : 'Your chart is ready to explore.');
        method.append(label, detail);
      }
      panel.hidden = false;
    };

    const renderBaziLuckCycles = (luckCycles, dayMaster) => {
      const panel = calculator.querySelector('[data-bazi-luck-cycles]');
      if (!panel) return;
      if (!luckCycles || !Array.isArray(luckCycles.cycles)) { panel.hidden = true; return; }
      const fullCycles = luckCycles.cycles.filter((cycle) => !cycle.is_start_segment && cycle.gan_zhi);
      if (!fullCycles.length) { panel.hidden = true; return; }

      const onset = luckCycles.onset || {};
      const onsetParts = [
        Number(onset.years) ? `${onset.years} years` : '',
        Number(onset.months) ? `${onset.months} months` : '',
        Number(onset.days) ? `${onset.days} days` : ''
      ].filter(Boolean);
      const summary = panel.querySelector('[data-bazi-luck-cycles-summary]');
      if (summary) summary.textContent = onsetParts.length ? `Your first Horoscope Cycle begins ${onsetParts.join(', ')} after birth — each cycle then colours roughly a decade of your life.` : '';
      const context = panel.querySelector('[data-bazi-luck-cycles-context]');
      if (context) context.hidden = true;

      const currentYear = new Date().getFullYear();
      const list = panel.querySelector('[data-bazi-luck-cycles-list]');
      let currentItem = null;
      if (list) {
        list.replaceChildren();
        fullCycles.forEach((cycle, index) => {
          const item = document.createElement('li');
          const currentFlag = document.createElement('em');
          const age = document.createElement('b');
          const years = document.createElement('span');
          const pillar = document.createElement('b');
          const details = document.createElement('small');
          const startYear = Number(cycle.start_year);
          const endYear = Number(cycle.end_year);
          const startAge = Number(cycle.start_age);
          const endAge = Number(cycle.end_age);
          const isCurrent = Number.isFinite(startYear) && Number.isFinite(endYear) && currentYear >= startYear && currentYear <= endYear;
          item.dataset.current = String(isCurrent);
          currentFlag.className = 'bazi-luck-cycles__current-flag';
          age.className = 'bazi-luck-cycles__age';
          years.className = 'bazi-luck-cycles__years';
          pillar.className = 'bazi-luck-cycles__pillar';
          details.className = 'bazi-luck-cycles__details';
          currentFlag.textContent = 'Now';
          age.textContent = Number.isFinite(startAge) ? `Age ${startAge}` : `Cycle ${index + 1}`;
          years.textContent = Number.isFinite(startYear) ? String(startYear) : '';
          const ganZhi = String(cycle.gan_zhi || '');
          pillar.textContent = [
            [cycle.stem?.pinyin, cycle.stem?.element].filter(Boolean).join(' '),
            [cycle.branch?.pinyin, cycle.branch?.element].filter(Boolean).join(' ')
          ].filter(Boolean).join('\n');
          details.textContent = ganZhi || [cycle.stem?.hanzi, cycle.branch?.hanzi].filter(Boolean).join('');
          item.append(currentFlag, age, years, pillar, details);
          list.append(item);
          if (isCurrent) currentItem = item;
        });
        if (currentItem && typeof currentItem.scrollIntoView === 'function') {
          window.requestAnimationFrame(() => {
            currentItem.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'auto' });
          });
        }
      }

      const note = panel.querySelector('[data-bazi-luck-cycles-note]');
      if (note) {
        const currentCycle = fullCycles.find((cycle) => {
          const startYear = Number(cycle.start_year);
          const endYear = Number(cycle.end_year);
          return Number.isFinite(startYear) && Number.isFinite(endYear) && currentYear >= startYear && currentYear <= endYear;
        });
        let noteText = '';
        if (currentCycle && dayMaster?.element) {
          const stemRelation = baziElementRelation(dayMaster.element, currentCycle.stem?.element);
          const branchRelation = baziElementRelation(dayMaster.element, currentCycle.branch?.element);
          const stemTheme = BAZI_RELATION_THEMES[stemRelation];
          const branchTheme = BAZI_RELATION_THEMES[branchRelation];
          if (stemTheme) {
            const masterName = [dayMaster.yin_yang, dayMaster.element].filter(Boolean).join(' ');
            noteText = `You are in your ${currentCycle.gan_zhi} cycle now (${currentCycle.start_year}–${currentCycle.end_year}). For your ${masterName} Day Master, the ${currentCycle.stem?.element || ''} stem of this decade traditionally emphasises ${stemTheme}`;
            noteText += branchTheme && branchRelation !== stemRelation
              ? `, while the ${currentCycle.branch?.element || ''} branch adds an undercurrent of ${branchTheme}.`
              : ', reinforced by the branch beneath it.';
            noteText += ' How strongly this plays out depends on your whole chart — that interaction is what a full reading maps.';
          }
        } else if (!currentCycle && fullCycles.length) {
          const firstCycle = fullCycles[0];
          const firstStart = Number(firstCycle.start_year);
          if (Number.isFinite(firstStart) && currentYear < firstStart) {
            noteText = `Your first cycle begins in ${firstStart} (around age ${firstCycle.start_age}). Until then, tradition reads the Month Pillar as setting the tone.`;
          }
        }
        note.textContent = noteText;
        note.hidden = !noteText;
      }

      panel.hidden = false;
    };

    const renderBaziZodiac = (zodiac) => {
      const panel = calculator.querySelector('[data-bazi-zodiac-hero]');
      if (!panel || !zodiac?.animal) return;
      const title = panel.querySelector('[data-bazi-zodiac-title]');
      const image = panel.querySelector('[data-bazi-zodiac-image]');
      if (title) title.textContent = zodiac.animal;
      setBaziZodiacImage(image, zodiac.animal);
      if (image?.getAttribute('src')) {
        result.style.setProperty('--bazi-zodiac-art', `url("${image.src}")`);
        result.classList.add('has-zodiac');
      }
      panel.hidden = false;
    };

    const renderBaziStructure = (chart) => {
      const panel = calculator.querySelector('[data-bazi-structure]');
      if (!panel || !chart) return;
      const visibleCharacters = ['year', 'month', 'day', 'hour'].flatMap((key) => [chart[key]?.stem, chart[key]?.branch]).filter(Boolean);
      const yinCount = visibleCharacters.filter((character) => String(character.yin_yang || '').toLowerCase() === 'yin').length;
      const yangCount = visibleCharacters.filter((character) => String(character.yin_yang || '').toLowerCase() === 'yang').length;
      const setText = (selector, value) => { const target = panel.querySelector(selector); if (target) target.textContent = value || '—'; };
      setText('[data-bazi-polarity-yin]', yinCount);
      setText('[data-bazi-polarity-yang]', yangCount);
      setText('[data-bazi-polarity-note]', `${visibleCharacters.length} visible characters · stems and branches together`);
      const monthBranch = chart.month?.branch;
      if (monthBranch) {
        setBaziElementIcon(panel.querySelector('[data-bazi-season-icon]'), monthBranch.element);
        setText('[data-bazi-season-title]', `${monthBranch.pinyin} · ${monthBranch.element}`);
        setText('[data-bazi-season-note]', `${monthBranch.pinyin} (${monthBranch.hanzi}) · ${monthBranch.yin_yang} ${monthBranch.element} · Month Branch seasonal setting`);
      }
      const index = panel.querySelector('[data-bazi-character-index]');
      if (index) {
        index.replaceChildren();
        const pillarLabels = { year: 'Year', month: 'Month', day: 'Day', hour: 'Hour' };
        ['year', 'month', 'day', 'hour'].forEach((key) => {
          const pillar = chart[key];
          if (!pillar) return;
          [{ label: 'Heavenly Stem', character: pillar.stem, type: 'stem' }, { label: 'Earthly Branch', character: pillar.branch, type: 'branch' }].forEach(({ label, character, type }) => {
            if (!character) return;
            const item = document.createElement('li');
            const art = document.createElement('i');
            const location = document.createElement('small');
            const name = document.createElement('b');
            const notation = document.createElement('span');
            const detail = document.createElement('p');
            item.dataset.element = String(character.element || '').toLowerCase();
            item.dataset.kind = type;
            location.textContent = `${pillarLabels[key]} · ${label}`;
            name.textContent = `${character.yin_yang} ${character.element}`;
            notation.textContent = `${character.pinyin} · ${character.hanzi}`;
            detail.textContent = `${character.yin_yang} ${character.element}`;
            art.setAttribute('aria-hidden', 'true');
            setBaziElementIcon(art, character.element);
            item.append(art, location, name, notation, detail);
            index.append(item);
          });
        });
      }
      panel.hidden = false;
    };

    const renderBaziTraditionalLayers = (analysis) => {
      const panel = calculator.querySelector('[data-bazi-traditional-layers]');
      if (!panel || !analysis) return;
      const hiddenStems = Array.isArray(analysis.hidden_stems) ? analysis.hidden_stems : [];
      const visibleRelationships = Array.isArray(analysis.visible_relationships) ? analysis.visible_relationships : [];
      const branchRelations = Array.isArray(analysis.branch_relations) ? analysis.branch_relations : [];
      if (!hiddenStems.length && !visibleRelationships.length && !branchRelations.length) { panel.hidden = true; return; }
      const hiddenSection = panel.querySelector('.bazi-hidden-stems');
      const relationshipsSection = panel.querySelector('.bazi-visible-relationships');
      const branchSection = panel.querySelector('.bazi-branch-relations');
      const summary = panel.querySelector('.bazi-detail-drawer summary small');
      if (hiddenSection) hiddenSection.hidden = !hiddenStems.length;
      if (relationshipsSection) relationshipsSection.hidden = !visibleRelationships.length;
      if (branchSection) branchSection.hidden = !branchRelations.length;
      if (summary) {
        const relationParts = [
          `${hiddenStems.length} branches with hidden stems`,
          `${visibleRelationships.length} labels to your Day Master`
        ];
        if (branchRelations.length) relationParts.push(`${branchRelations.length} visible branch pair${branchRelations.length === 1 ? '' : 's'}`);
        summary.textContent = relationParts.join(' · ');
      }
      const hiddenTarget = panel.querySelector('[data-bazi-hidden-stems]');
      if (hiddenTarget) {
        hiddenTarget.replaceChildren();
        hiddenStems.forEach((entry) => {
          const card = document.createElement('article');
          const branchImage = document.createElement('img');
          const heading = document.createElement('div');
          const label = document.createElement('small');
          const title = document.createElement('b');
          const stems = document.createElement('div');
          branchImage.alt = ''; branchImage.width = 320; branchImage.height = 320; branchImage.hidden = true;
          setBaziElementImage(branchImage, entry.branch?.element);
          label.textContent = `${entry.pillar || 'Pillar'} Branch`;
          title.textContent = `${entry.branch?.element || 'Element'} · ${entry.branch?.hanzi || ''}`;
          heading.append(label, title);
          stems.className = 'bazi-hidden-stems__chips';
          (entry.stems || []).forEach((stem) => {
            const chip = document.createElement('span'); const icon = document.createElement('i'); const name = document.createElement('b'); const relation = document.createElement('small');
            chip.dataset.element = String(stem.element || '').toLowerCase(); icon.setAttribute('aria-hidden', 'true'); setBaziElementIcon(icon, stem.element);
            name.textContent = `${stem.pinyin} · ${stem.hanzi}`; relation.textContent = stem.label || '';
            chip.append(icon, name, relation); stems.append(chip);
          });
          card.append(branchImage, heading, stems); hiddenTarget.append(card);
        });
      }
      const relationshipsTarget = panel.querySelector('[data-bazi-visible-relationships]');
      if (relationshipsTarget) {
        relationshipsTarget.replaceChildren();
        visibleRelationships.forEach((entry) => {
          const item = document.createElement('article'); const location = document.createElement('small'); const name = document.createElement('b'); const relation = document.createElement('span');
          item.dataset.family = String(entry.family || '').toLowerCase(); location.textContent = `${entry.pillar || ''} · ${entry.layer || ''}`; name.textContent = `${entry.character?.pinyin || ''} · ${entry.character?.hanzi || ''}`; relation.textContent = entry.label || '';
          item.append(location, name, relation); relationshipsTarget.append(item);
        });
      }
      const branchTarget = panel.querySelector('[data-bazi-branch-relations]');
      if (branchTarget) {
        branchTarget.replaceChildren();
        branchRelations.forEach((pattern) => { const item = document.createElement('article'); const type = document.createElement('b'); const detail = document.createElement('span'); item.dataset.pattern = String(pattern.type || '').toLowerCase().replace(/\s+/g, '-'); type.textContent = pattern.type || 'Branch pattern'; detail.textContent = pattern.detail || ''; item.append(type, detail); branchTarget.append(item); });
      }
      panel.hidden = false;
    };

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      message.textContent = '';

      if (!form.reportValidity()) return;
      if (!endpoint) {
        message.textContent = 'The chart service has not been connected yet.';
        return;
      }

      const data = new FormData(form);
      const payload = {
        birth_date: data.get('birth_date'),
        birth_time: data.get('birth_time'),
        birth_time_known: !timeUnknown?.checked,
        birth_place: String(data.get('birth_place') || '').trim(),
        gender: data.get('gender'),
        email: String(data.get('email') || '').trim(),
        privacy_consent: data.get('privacy_consent') === 'yes',
        marketing_consent: data.get('marketing_consent') === 'yes'
      };

      setLoading(true);
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'We could not calculate this chart.');
        if (!body.chart) throw new Error('We could not calculate this chart.');
        const hasHour = Boolean(body.chart.hour);
        ['hour', 'day', 'month', 'year'].forEach((key) => {
          const card = calculator.querySelector(`[data-pillar="${key}"]`);
          if (card) card.hidden = !body.chart[key];
          renderPillar(key, body.chart[key]);
        });
        renderBaziZodiac(body.zodiac || body.chart.year?.branch);
        renderBaziAnalysis(body.analysis ? { ...body.analysis, day_master: body.analysis.day_master || body.chart.day?.stem } : body.analysis);
        renderBaziLuckCycles(body.luck_cycles, body.chart.day?.stem);
        const dayMaster = body.chart.day.stem;
        const resultTitle = calculator.querySelector('[data-bazi-result-title]');
        const resultType = calculator.querySelector('[data-bazi-result-type]');
        const resultMeta = calculator.querySelector('[data-bazi-result-meta]');
        const timeNote = calculator.querySelector('[data-bazi-time-note]');
        if (resultTitle) resultTitle.textContent = `${dayMaster.yin_yang} ${dayMaster.element} Day Master · ${dayMaster.pinyin}`;
        if (resultType) resultType.textContent = hasHour ? 'Four Pillars · 八字' : 'Three Pillars · 三柱';
        if (timeNote) timeNote.hidden = hasHour;
        if (resultMeta) resultMeta.textContent = `${body.birth.date}${body.birth.time ? ` · ${body.birth.time} local time` : ''} · ${body.birth.place}`;
        lastResponse = body;
        result.classList.remove('is-locked');
        if (lock) lock.hidden = true;
        if (reportActions) reportActions.hidden = false;
        result.classList.remove('has-result');
        requestAnimationFrame(() => result.classList.add('has-result'));
        message.textContent = hasHour ? 'Your Four Pillars are ready.' : 'Your Three Pillars are ready.';
        if (window.matchMedia('(max-width: 900px)').matches) {
          const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          result.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
        }
      } catch (error) {
        message.textContent = error.message || 'The chart service is temporarily unavailable. Please try again.';
      } finally {
        setLoading(false);
      }
    });
  });
};

const initWesternChart = (scope = document) => {
  scope.querySelectorAll('[data-western-chart]').forEach((calculator) => {
    const form = calculator.querySelector('[data-western-chart-form]');
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const endpoint = calculator.dataset.apiUrl;
    const message = calculator.querySelector('[data-western-chart-message]');
    const submit = calculator.querySelector('[data-western-chart-submit]');
    const knownTime = calculator.querySelector('[data-western-time-known]');
    const timeDetails = calculator.querySelector('[data-western-time-details]');
    const next = calculator.querySelector('[data-western-next]');
    const back = calculator.querySelector('[data-western-back]');
    const steps = [...calculator.querySelectorAll('[data-western-step]')];
    const progress = [...calculator.querySelectorAll('[data-western-progress]')];
    const empty = calculator.querySelector('[data-western-chart-empty]');
    const result = calculator.querySelector('[data-western-chart-result]');
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    const westernSignGlyph = { Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍', Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓' };
    const westernPlanetGlyph = { sun: '☉', moon: '☾', mercury: '☿', venus: '♀', mars: '♂', jupiter: '♃', saturn: '♄', uranus: '♅', neptune: '♆', pluto: '♇' };
    const planetGuide = { sun: 'conscious direction and what you cultivate', moon: 'emotional rhythm and familiar responses', mercury: 'thinking and communication style', venus: 'values, taste, and relating style', mars: 'drive, effort, and how you initiate', jupiter: 'growth, belief, and opportunities to expand', saturn: 'structure, responsibility, and long-term practice', uranus: 'change, independence, and experimentation', neptune: 'imagination, ideals, and permeability', pluto: 'depth, renewal, and shifting power dynamics' };
    const signMeta = {
      Aries: ['Fire', 'Cardinal'], Taurus: ['Earth', 'Fixed'], Gemini: ['Air', 'Mutable'], Cancer: ['Water', 'Cardinal'], Leo: ['Fire', 'Fixed'], Virgo: ['Earth', 'Mutable'], Libra: ['Air', 'Cardinal'], Scorpio: ['Water', 'Fixed'], Sagittarius: ['Fire', 'Mutable'], Capricorn: ['Earth', 'Cardinal'], Aquarius: ['Air', 'Fixed'], Pisces: ['Water', 'Mutable']
    };
    const svgNode = (tag, attributes = {}) => {
      const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
      Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, String(value)));
      return node;
    };
    const point = (longitude, radius) => {
      const angle = ((longitude - 90) * Math.PI) / 180;
      return { x: 130 + Math.cos(angle) * radius, y: 130 + Math.sin(angle) * radius };
    };
    const text = (selector, value) => {
      const target = calculator.querySelector(selector);
      if (target) target.textContent = value || '—';
    };
    const shortPlanetName = (name) => westernPlanetGlyph[String(name || '').toLowerCase()] || String(name || '').slice(0, 2);
    const renderWesternPlanets = (chart) => {
      const target = calculator.querySelector('[data-western-planets]');
      if (!target) return;
      target.replaceChildren();
      Object.values(chart.planets || {}).forEach((planet) => {
        const item = document.createElement('div'); item.className = 'western-planet-row';
        const term = document.createElement('dt'); const glyph = document.createElement('i'); const name = document.createElement('b'); const purpose = document.createElement('small'); const value = document.createElement('dd');
        const key = String(planet.name || '').toLowerCase(); glyph.textContent = shortPlanetName(planet.name); name.textContent = planet.name || 'Placement'; purpose.textContent = planetGuide[key] || 'a reference point in the chart'; value.textContent = `${planet.formatted || '—'}${planet.retrograde ? ' · retrograde' : ''}`;
        term.append(glyph, name, purpose); item.append(term, value); target.append(item);
      });
    };
    const renderWesternAspects = (chart) => {
      const target = calculator.querySelector('[data-western-aspects]');
      if (!target) return;
      target.replaceChildren();
      (chart.aspects?.major || []).forEach((aspect) => {
        const item = document.createElement('li'); item.className = 'western-aspect-row';
        const marker = document.createElement('i'); const bodies = document.createElement('b'); const detail = document.createElement('small');
        marker.textContent = aspect.symbol || '✦'; bodies.textContent = `${aspect.body_1 || '—'} ${aspect.body_2 || '—'}`; detail.textContent = `${aspect.type || 'major aspect'} · ${aspect.orb ?? '—'}° orb`;
        item.append(marker, bodies, detail); target.append(item);
      });
    };
    const renderWesternHouses = (chart) => {
      const target = calculator.querySelector('[data-western-houses]');
      if (!target) return;
      target.replaceChildren();
      (chart.houses?.cusps || []).forEach((cusp) => {
        const item = document.createElement('li'); const number = document.createElement('b'); const value = document.createElement('span');
        number.textContent = `House ${cusp.house}`; value.textContent = cusp.formatted || '—'; item.append(number, value); target.append(item);
      });
    };
    const renderWesternWheel = (chart) => {
      const wrap = calculator.querySelector('[data-western-wheel-wrap]');
      const svg = calculator.querySelector('[data-western-wheel]');
      const planets = Object.values(chart.planets || {}).filter((planet) => signs.includes(planet.sign));
      if (!wrap || !svg || !planets.length) return;
      svg.replaceChildren();
      [112, 88, 42].forEach((radius, index) => svg.append(svgNode('circle', { cx: 130, cy: 130, r: radius, class: `western-wheel__ring western-wheel__ring--${index + 1}` })));
      signs.forEach((sign, index) => {
        const lineStart = point(index * 30, 112); const lineEnd = point(index * 30, 88); const label = point(index * 30 + 15, 100);
        svg.append(svgNode('line', { x1: lineStart.x, y1: lineStart.y, x2: lineEnd.x, y2: lineEnd.y, class: 'western-wheel__tick' }));
        const labelNode = svgNode('text', { x: label.x, y: label.y, class: 'western-wheel__sign', 'text-anchor': 'middle', 'dominant-baseline': 'middle' });
        labelNode.textContent = westernSignGlyph[sign]; svg.append(labelNode);
      });
      const positioned = planets.map((planet, index) => {
        const degree = signs.indexOf(planet.sign) * 30 + Number(planet.degree || 0) + Number(planet.minute || 0) / 60;
        return { planet, point: point(degree, 67 - (index % 3) * 12) };
      });
      const byName = new Map(positioned.map((entry) => [String(entry.planet.name || '').toLowerCase(), entry.point]));
      (chart.aspects?.major || []).slice(0, 10).forEach((aspect) => {
        const start = byName.get(String(aspect.body_1 || '').toLowerCase()); const end = byName.get(String(aspect.body_2 || '').toLowerCase());
        if (!start || !end) return;
        const type = String(aspect.type || 'major').toLowerCase().replace(/[^a-z]+/g, '-');
        svg.append(svgNode('line', { x1: start.x, y1: start.y, x2: end.x, y2: end.y, class: `western-wheel__aspect western-wheel__aspect--${type}` }));
      });
      positioned.forEach(({ planet, point: marker }) => {
        svg.append(svgNode('circle', { cx: marker.x, cy: marker.y, r: 9, class: 'western-wheel__planet' }));
        const label = svgNode('text', { x: marker.x, y: marker.y + .5, class: 'western-wheel__planet-label', 'text-anchor': 'middle', 'dominant-baseline': 'middle' });
        label.textContent = shortPlanetName(planet.name); svg.append(label);
      });
      const ascendant = chart.angles?.ascendant;
      if (ascendant && signs.includes(ascendant.sign)) {
        const degree = signs.indexOf(ascendant.sign) * 30 + Number(ascendant.degree || 0) + Number(ascendant.minute || 0) / 60;
        const start = point(degree, 114); const end = point(degree, 126); const label = point(degree, 134);
        svg.append(svgNode('line', { x1: start.x, y1: start.y, x2: end.x, y2: end.y, class: 'western-wheel__ascendant-line' }));
        const asc = svgNode('text', { x: label.x, y: label.y, class: 'western-wheel__ascendant', 'text-anchor': 'middle', 'dominant-baseline': 'middle' }); asc.textContent = 'ASC'; svg.append(asc);
      }
      const title = svgNode('text', { x: 130, y: 130, class: 'western-wheel__center', 'text-anchor': 'middle' }); title.textContent = 'Birth chart'; svg.append(title);
      wrap.hidden = false;
    };
    const renderWesternSignature = (chart) => {
      const section = calculator.querySelector('[data-western-signature]');
      const elementTarget = calculator.querySelector('[data-western-element-balance]');
      const modalityTarget = calculator.querySelector('[data-western-modality-balance]');
      const placements = Object.values(chart.planets || {}).filter((planet) => signMeta[planet.sign]);
      if (!section || !elementTarget || !modalityTarget || !placements.length) return;
      const elementCounts = { Fire: 0, Earth: 0, Air: 0, Water: 0 }; const modalityCounts = { Cardinal: 0, Fixed: 0, Mutable: 0 };
      placements.forEach((planet) => { const [element, modality] = signMeta[planet.sign]; elementCounts[element] += 1; modalityCounts[modality] += 1; });
      const renderCounts = (target, counts) => {
        target.replaceChildren();
        Object.entries(counts).forEach(([name, count]) => { const item = document.createElement('div'); const number = document.createElement('b'); const label = document.createElement('span'); const note = document.createElement('small'); item.className = 'western-signature-count'; item.dataset.value = name.toLowerCase(); number.textContent = String(count); label.textContent = name; note.textContent = `${count} of ${placements.length} placements`; item.append(number, label, note); target.append(item); });
      };
      renderCounts(elementTarget, elementCounts); renderCounts(modalityTarget, modalityCounts); section.hidden = false;
    };
    const renderWesternZodiacArt = (chart) => {
      const target = calculator.querySelector('[data-western-zodiac-art]');
      const sign = chart.planets?.sun?.sign;
      const name = String(sign || '').toLowerCase();
      const key = `zodiac${name.charAt(0).toUpperCase()}${name.slice(1)}`;
      const asset = target?.dataset[key];
      if (!target || !asset) return;
      target.style.backgroundImage = `url("${asset}")`;
      target.dataset.sign = name;
      target.hidden = false;
    };
    const updateKnownTime = () => {
      const hasExactTime = knownTime?.checked !== false;
      if (timeDetails) timeDetails.hidden = !hasExactTime;
      timeDetails?.querySelectorAll('input').forEach((input) => { input.disabled = !hasExactTime; });
    };
    knownTime?.addEventListener('change', updateKnownTime); updateKnownTime();
    const showStep = (number) => {
      steps.forEach((step) => {
        const isActive = step.dataset.westernStep === String(number);
        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
      });
      progress.forEach((item) => item.classList.toggle('is-current', item.dataset.westernProgress === String(number)));
      if (message) message.textContent = '';
      if (number === 2) calculator.querySelector('input[name="email"]')?.focus({ preventScroll: true });
    };
    const validateStep = (step) => {
      const invalid = [...step.querySelectorAll('input, select, textarea')].find((control) => !control.disabled && !control.checkValidity());
      if (!invalid) return true;
      invalid.reportValidity();
      return false;
    };
    next?.addEventListener('click', () => {
      const firstStep = calculator.querySelector('[data-western-step="1"]');
      if (firstStep && validateStep(firstStep)) showStep(2);
    });
    back?.addEventListener('click', () => showStep(1));
    form.addEventListener('submit', async (event) => {
      event.preventDefault(); if (message) message.textContent = ''; if (!form.reportValidity()) return;
      if (!endpoint) { if (message) message.textContent = 'The Western chart service has not been connected yet.'; return; }
      const data = new FormData(form); const payload = { birth_date: data.get('birth_date'), birth_time: data.get('birth_time'), birth_time_known: data.get('birth_time_known') === 'yes', birth_place: String(data.get('birth_place') || '').trim(), email: String(data.get('email') || '').trim(), privacy_consent: data.get('privacy_consent') === 'yes', marketing_consent: data.get('marketing_consent') === 'yes' };
      submit.disabled = true; submit.textContent = 'CALCULATING YOUR CHART…';
      try {
        const response = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }); const body = await response.json().catch(() => ({}));
        if (!response.ok || !body.chart) throw new Error(body.error || 'We could not calculate this chart.');
        const chart = body.chart; const sun = chart.planets?.sun; const moon = chart.planets?.moon; const rising = chart.angles?.ascendant;
        text('[data-western-chart-title]', rising ? 'Your Sun, Moon and Rising' : 'Your Sun and Moon placements'); text('[data-western-chart-meta]', `${body.birth.date}${body.birth.time ? ` · ${body.birth.time} local time` : ' · birth time unknown'}${body.birth.place ? ` · ${body.birth.place}` : ''}`); text('[data-western-sun]', sun?.formatted); text('[data-western-moon]', moon?.formatted); text('[data-western-rising]', rising?.formatted || 'Not calculated');
        renderWesternZodiacArt(chart); renderWesternWheel(chart); renderWesternSignature(chart); renderWesternPlanets(chart); renderWesternAspects(chart); renderWesternHouses(chart);
        const houses = calculator.querySelector('[data-western-houses-wrap]'); if (houses) houses.hidden = !(chart.houses?.cusps || []).length; if (empty) empty.hidden = true; if (result) result.hidden = false;
        if (message) message.textContent = 'Your Western chart is ready.';
      } catch (error) { if (message) message.textContent = error.message || 'The chart service is temporarily unavailable. Please try again.'; }
      finally { submit.disabled = false; submit.textContent = 'CALCULATE MY WESTERN CHART'; }
    });
  });
};

const getLocalDateValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initDailyOutlook = (scope = document) => {
  const outlookApi = window.PhoseiDailyOutlook;
  scope.querySelectorAll('[data-daily-outlook]').forEach((outlook) => {
    const form = outlook.querySelector('[data-daily-outlook-form]');
    const input = form?.querySelector('input[name="daily_birth_date"]');
    const result = outlook.querySelector('[data-daily-outlook-result]');
    const message = outlook.querySelector('[data-daily-outlook-message]');
    if (!form || !input || !result || !message || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';

    const today = getLocalDateValue();
    input.max = today;
    const chartForm = document.querySelector('[data-bazi-form]');
    const chartBirthDate = chartForm?.querySelector('input[name="birth_date"]');
    const chartLink = outlook.querySelector('[data-daily-outlook-chart-link]');

    if (chartBirthDate?.value) input.value = chartBirthDate.value;
    chartBirthDate?.addEventListener('input', () => {
      input.value = chartBirthDate.value;
    });

    chartLink?.addEventListener('click', () => {
      if (chartBirthDate && input.value) chartBirthDate.value = input.value;
      window.setTimeout(() => chartBirthDate?.focus({ preventScroll: true }), 350);
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      message.textContent = '';
      result.hidden = true;

      if (!form.reportValidity()) return;
      if (!outlookApi) {
        message.textContent = 'Daily Outlook is temporarily unavailable. You can still use the full BaZi calculator above.';
        return;
      }

      try {
        const daily = outlookApi.getDailyOutlook(input.value, today);
        const displayDate = new Intl.DateTimeFormat('en', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(`${daily.date}T12:00:00`));
        const values = {
          '[data-daily-outlook-date]': displayDate,
          '[data-daily-outlook-theme]': daily.theme,
          '[data-daily-outlook-sun-sign]': daily.sunSign.name,
          '[data-daily-outlook-zodiac]': `${daily.zodiac.animal} · ${daily.zodiac.branch}`,
          '[data-daily-outlook-western]': daily.westernLens,
          '[data-daily-outlook-chinese]': daily.zodiacLens,
          '[data-daily-outlook-shared]': daily.sharedPattern,
          '[data-daily-outlook-prompt]': daily.practicalPrompt
        };

        Object.entries(values).forEach(([selector, value]) => {
          const target = result.querySelector(selector);
          if (target) target.textContent = value;
        });
        result.hidden = false;
        result.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      } catch (error) {
        message.textContent = error instanceof Error ? error.message : 'Enter a valid birth date.';
      }
    });
  });
};

const initPremiumScroll = () => {
  if (window.__phoseiScrollCleanup) window.__phoseiScrollCleanup();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktopMotion = window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)').matches;
  const header = document.querySelector('.pp-header');
  const hero = document.querySelector('[data-scroll-hero]');
  const chapter = document.querySelector('[data-scroll-chapter]');
  const chapterSteps = chapter ? [...chapter.querySelectorAll('.decision-step')] : [];
  const lineReveal = document.querySelector('[data-line-reveal]');
  const counters = [...document.querySelectorAll('[data-count-to]')];
  const chapterRoot = document.querySelector('[data-chapter-scroll-root]');
  const chapters = chapterRoot ? [...chapterRoot.querySelectorAll('[data-home-chapter]')].filter((item) => !item.closest('[hidden]')) : [];
  const chapterNav = document.querySelector('[data-chapter-nav]');
  const chapterButtons = chapterNav ? [...chapterNav.querySelectorAll('[data-chapter-target]')] : [];
  const currentNumber = chapterNav?.querySelector('[data-chapter-current]');
  const currentTitle = chapterNav?.querySelector('[data-chapter-current-title]');
  const motionStrength = chapterRoot?.dataset.motionStrength || 'balanced';
  const motionOptions = {
    subtle: { lerp: 0.115, snapDuration: 0.65, snapDistance: '18%', countDuration: 760 },
    balanced: { lerp: 0.09, snapDuration: 0.82, snapDistance: '28%', countDuration: 900 },
    strong: { lerp: 0.075, snapDuration: 1, snapDistance: '36%', countDuration: 1040 }
  }[motionStrength] || { lerp: 0.09, snapDuration: 0.82, snapDistance: '28%', countDuration: 900 };
  const observers = [];
  const animationFrames = new Set();
  const cleanupCallbacks = [];
  let lenis = null;
  let snap = null;
  let scrollFrame = 0;
  let activeChapterIndex = -1;

  const chapterScrollEnabled = chapterRoot?.dataset.chapterScrollEnabled === 'true';
  const canUseChapterScroll = Boolean(
    chapterScrollEnabled && desktopMotion && !reducedMotion && chapters.length && window.Lenis && window.Snap
  );

  if (chapterRoot) {
    chapterRoot.classList.toggle('has-chapter-scroll', canUseChapterScroll);
    chapterRoot.classList.remove('motion-subtle', 'motion-balanced', 'motion-strong');
    chapterRoot.classList.add(`motion-${motionStrength}`);
  }

  const setStaticState = () => {
    if (hero) hero.style.setProperty('--hero-scroll', '0');
    if (lineReveal) lineReveal.classList.add('is-drawn');
    chapterSteps.forEach((step) => step.classList.add('is-current'));
    counters.forEach((counter) => {
      counter.textContent = `${counter.dataset.countTo || counter.textContent}${counter.dataset.countSuffix || ''}`;
    });
  };

  const updateScrollState = () => {
    scrollFrame = 0;
    const scrollY = window.scrollY || window.pageYOffset;

    if (header) header.classList.toggle('is-scrolled', scrollY > 28);

    if (hero) {
      const heroProgress = Math.min(Math.max(scrollY / Math.max(hero.offsetHeight, 1), 0), 1);
      hero.style.setProperty('--hero-scroll', heroProgress.toFixed(4));
    }

    if (chapters.length) {
      const marker = window.innerHeight * 0.42;
      let nextActiveIndex = 0;
      chapters.forEach((homeChapter, index) => {
        if (homeChapter.getBoundingClientRect().top <= marker) nextActiveIndex = index;
      });
      if (window.innerHeight + scrollY >= document.documentElement.scrollHeight - 4) {
        nextActiveIndex = chapters.length - 1;
      }

      if (nextActiveIndex !== activeChapterIndex) {
        activeChapterIndex = nextActiveIndex;
        chapters.forEach((homeChapter, index) => {
          homeChapter.classList.toggle('is-past', index < activeChapterIndex);
          homeChapter.classList.toggle('is-active', index === activeChapterIndex);
          homeChapter.classList.toggle('is-next', index === activeChapterIndex + 1);
        });
        chapterButtons.forEach((button, index) => {
          const isCurrent = index === activeChapterIndex;
          button.classList.toggle('is-current', isCurrent);
          if (isCurrent) button.setAttribute('aria-current', 'step');
          else button.removeAttribute('aria-current');
        });
        const activeChapter = chapters[activeChapterIndex];
        const chapterColor = window.getComputedStyle(activeChapter).getPropertyValue('--chapter-color').trim();
        chapterRoot?.style.setProperty('--active-chapter-accent', chapterColor || '#c9b186');
        chapterRoot?.style.setProperty('--chapter-progress', chapters.length > 1 ? (activeChapterIndex / (chapters.length - 1)).toFixed(4) : '1');
        if (currentNumber) currentNumber.textContent = String(activeChapterIndex + 1).padStart(2, '0');
        if (currentTitle) currentTitle.textContent = activeChapter?.dataset.chapterTitle || '';
      }
    }

    if (chapter && chapterSteps.length) {
      const rect = chapter.getBoundingClientRect();
      const travel = rect.height + window.innerHeight * 0.35;
      const rawProgress = (window.innerHeight * 0.72 - rect.top) / Math.max(travel, 1);
      const chapterProgress = Math.min(Math.max(rawProgress, 0), 0.999);
      const currentIndex = Math.floor(chapterProgress * chapterSteps.length);
      chapterSteps.forEach((step, index) => step.classList.toggle('is-current', index <= currentIndex));
    }
  };

  const requestScrollUpdate = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(updateScrollState);
  };

  if (canUseChapterScroll) {
    lenis = new window.Lenis({
      autoRaf: true,
      lerp: motionOptions.lerp,
      smoothWheel: true,
      syncTouch: false,
      anchors: { offset: -88, duration: motionOptions.snapDuration },
      stopInertiaOnNavigate: true,
      prevent: (node) => node.matches?.('input, textarea, select, button, [contenteditable="true"], [data-lenis-prevent]')
    });
    snap = new window.Snap(lenis, {
      type: 'proximity',
      distanceThreshold: motionOptions.snapDistance,
      debounce: 220,
      duration: motionOptions.snapDuration,
      easing: (value) => 1 - Math.pow(1 - value, 4)
    });
    chapters.forEach((homeChapter) => snap.addElement(homeChapter, { align: 'start' }));
    lenis.on('scroll', requestScrollUpdate);
    window.__phoseiLenis = lenis;
  } else {
    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    cleanupCallbacks.push(() => window.removeEventListener('scroll', requestScrollUpdate));
  }

  window.addEventListener('resize', requestScrollUpdate, { passive: true });
  cleanupCallbacks.push(() => window.removeEventListener('resize', requestScrollUpdate));
  updateScrollState();

  if (window.location.hash) {
    const anchorTarget = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
    if (anchorTarget) {
      const anchorFrame = window.requestAnimationFrame(() => {
        if (lenis) lenis.scrollTo(anchorTarget, { immediate: true, offset: -88 });
        else anchorTarget.scrollIntoView({ behavior: 'auto', block: 'start' });
        updateScrollState();
      });
      animationFrames.add(anchorFrame);
    }
  }

  chapterButtons.forEach((button) => {
    const onChapterClick = () => {
      const target = document.getElementById(button.dataset.chapterTarget);
      if (!target) return;
      if (lenis) lenis.scrollTo(target, { duration: motionOptions.snapDuration, offset: -78 });
      else target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
    };
    button.addEventListener('click', onChapterClick);
    cleanupCallbacks.push(() => button.removeEventListener('click', onChapterClick));
  });

  if (snap) {
    const interactiveAreas = [...document.querySelectorAll('[data-scroll-interactive]')];
    interactiveAreas.forEach((area) => {
      const pauseSnap = () => snap?.stop();
      const resumeSnap = (event) => {
        if (event?.type === 'focusout' && area.contains(event.relatedTarget)) return;
        snap?.start();
      };
      area.addEventListener('pointerenter', pauseSnap);
      area.addEventListener('pointerleave', resumeSnap);
      area.addEventListener('focusin', pauseSnap);
      area.addEventListener('focusout', resumeSnap);
      cleanupCallbacks.push(() => {
        area.removeEventListener('pointerenter', pauseSnap);
        area.removeEventListener('pointerleave', resumeSnap);
        area.removeEventListener('focusin', pauseSnap);
        area.removeEventListener('focusout', resumeSnap);
      });
    });
  }

  const mediaQueries = [
    window.matchMedia('(prefers-reduced-motion: reduce)'),
    window.matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)')
  ];
  const handleMediaChange = () => window.requestAnimationFrame(initPremiumScroll);
  mediaQueries.forEach((query) => query.addEventListener?.('change', handleMediaChange));
  cleanupCallbacks.push(() => mediaQueries.forEach((query) => query.removeEventListener?.('change', handleMediaChange)));

  if (reducedMotion) {
    setStaticState();
  } else if (lineReveal && 'IntersectionObserver' in window) {
    const lineObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-drawn');
        lineObserver.unobserve(entry.target);
      });
    }, { threshold: 0.28 });
    lineObserver.observe(lineReveal);
    observers.push(lineObserver);
  } else if (lineReveal) {
    lineReveal.classList.add('is-drawn');
  }

  if (!reducedMotion && counters.length && 'IntersectionObserver' in window) {
    const counterObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.target.dataset.counted === 'true') return;
        const element = entry.target;
        const target = Number(element.dataset.countTo || 0);
        const suffix = element.dataset.countSuffix || '';
        const startTime = performance.now();
        element.dataset.counted = 'true';

        const animate = (now) => {
          const elapsed = Math.min((now - startTime) / motionOptions.countDuration, 1);
          const eased = 1 - Math.pow(1 - elapsed, 3);
          element.textContent = `${Math.round(target * eased)}${suffix}`;
          if (elapsed < 1) {
            const id = window.requestAnimationFrame(animate);
            animationFrames.add(id);
          }
        };

        element.textContent = `0${suffix}`;
        const id = window.requestAnimationFrame(animate);
        animationFrames.add(id);
        counterObserver.unobserve(element);
      });
    }, { threshold: 0.6 });
    counters.forEach((counter) => counterObserver.observe(counter));
    observers.push(counterObserver);
  }

  window.__phoseiScrollCleanup = () => {
    if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
    animationFrames.forEach((id) => window.cancelAnimationFrame(id));
    observers.forEach((observer) => observer.disconnect());
    cleanupCallbacks.forEach((callback) => callback());
    snap?.destroy();
    lenis?.destroy();
    delete window.__phoseiLenis;
    chapterRoot?.classList.remove('has-chapter-scroll');
  };
};

const initZodiacLookup = (scope = document) => {
  const signs = [
    { branch: '子', animal: 'Rat' },
    { branch: '丑', animal: 'Ox' },
    { branch: '寅', animal: 'Tiger' },
    { branch: '卯', animal: 'Rabbit' },
    { branch: '辰', animal: 'Dragon' },
    { branch: '巳', animal: 'Snake' },
    { branch: '午', animal: 'Horse' },
    { branch: '未', animal: 'Goat' },
    { branch: '申', animal: 'Monkey' },
    { branch: '酉', animal: 'Rooster' },
    { branch: '戌', animal: 'Dog' },
    { branch: '亥', animal: 'Pig' }
  ];

  scope.querySelectorAll('[data-zodiac-lookup]').forEach((lookup) => {
    const form = lookup.querySelector('[data-zodiac-form]');
    if (!form || form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const result = lookup.querySelector('[data-zodiac-result]');
    const message = lookup.querySelector('[data-zodiac-message]');

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const year = Number(new FormData(form).get('year'));
      if (!Number.isInteger(year) || year < 1900 || year > 2099) {
        message.textContent = 'Enter a year between 1900 and 2099.';
        result.hidden = true;
        return;
      }

      const sign = signs[((year - 2020) % 12 + 12) % 12];
      lookup.querySelector('[data-zodiac-branch]').textContent = sign.branch;
      lookup.querySelector('[data-zodiac-animal]').textContent = `${year}: Year of the ${sign.animal}`;
      lookup.querySelector('[data-zodiac-copy]').textContent = 'This is a year-only result. If your birthday falls in January or early February, use the full date calculator because the Chinese year does not begin on January 1.';
      setZodiacIcon(lookup.querySelector('[data-zodiac-icon]'), sign.animal);
      message.textContent = '';
      result.hidden = false;
    });
  });
};

const initTheme = (scope = document) => {
  initLocalPreviewLinks(scope);
  initReveal(scope);
  initMobileNavigation(scope);
  initDropdownNavigation(scope);
  initFooterYear(scope);
  initBaziCalculator(scope);
  initWesternChart(scope);
  initDailyOutlook(scope);
  initZodiacLookup(scope);
  initPremiumScroll();
};

document.addEventListener('DOMContentLoaded', () => initTheme());
document.addEventListener('shopify:section:load', (event) => initTheme(event.target));
